import React from "react";
import { RouteComponentProps } from "react-router";

import { QuestionnaireViewInner } from "./QuestionnaireView";
import ProgressBar from "../ProgressBar";
import Checkbox from "../Checkbox";

import { V1 } from "../../Api";
import { getSetStateAsync, formatName, formatDateLocal } from "../../Utilities";
import withTitle from "../withTitle";

interface IQuestionnairePrintAllViewState {
    surveySummary: any[];
    surveys: any[];
    loadProgress: number;
    loadTotal: number;
    loadError: string;
}

class QuestionnairePrintAllView extends React.Component<RouteComponentProps<any>, IQuestionnairePrintAllViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IQuestionnairePrintAllViewState = {
            surveySummary: [],
            surveys: [],
            loadProgress: 0,
            loadTotal: 0,
            loadError: "",
        };

        this.state = state;

        this.printPage = this.printPage.bind(this);
        this.selectAllSurveys = this.selectAllSurveys.bind(this);
        this.loadSurveys = this.loadSurveys.bind(this);
    }

    public async componentDidMount() {
        const fairId = this.props.match.params.id;

        try {
            const surveySummary = await V1.get<any[]>(`fairs/${fairId}/surveys/summary?include=extended`);
            for (const survey of surveySummary) {
                survey.isSelected = true;
            }

            this.setState({
                surveySummary: surveySummary.sort((a, b) =>
                    a.insurance.provider.localeCompare(b.insurance.provider) ||
                    a.address.city.localeCompare(b.address.city) ||
                    a.name.first.localeCompare(b.name.first)
                )
            });
        }
        catch (e) {
            this.setState({
                loadError: e.message || "Could not load list of surveys"
            });
        }
    }

    public render() {
        const propsBase: any = {
            displayContactInfo: false,
            submitContactNote: async () => { },
            userDisplayName: "",
        };

        return (
            <div className="viewer-root">
                <div className="print-hide">
                    {this.state.loadTotal > 0 ? <ProgressBar value={this.state.loadProgress * 100 / this.state.loadTotal} /> : null}
                    <div>
                        {this.state.loadTotal === 0
                            ? <div>
                                <span>Select the questionnaires you want to batch print.</span>
                                <br />
                                <br />
                                <table className="table-vertical fill-space">
                                    <thead>
                                        <tr>
                                            <th className="col-smallest"><Checkbox id="check-all" className="white" fancy={true} checked={this.state.surveySummary.every(a => a.isSelected)} onChange={this.selectAllSurveys} /></th>
                                            <th>Name</th>
                                            <th>Birthdate</th>
                                            <th>City</th>
                                            <th>Insurance</th>
                                            <th className="col-smallest">Sex</th>
                                            <th className="col-smallest col-center" title="Has appointment?"><i className="icon-calendar" /></th>
                                        </tr>
                                    </thead>
                                    <thead className="table-vertical-head-small">
                                        <tr><th colSpan={10}>Questionnaires</th></tr>
                                    </thead>
                                    <tbody>
                                        {this.state.surveySummary.map((a: any) => <tr key={a.id} className="clickable" onClick={() => this.selectSurvey(a)}>
                                            <td className="col-center"><Checkbox id={a.id} name={a.id} fancy={true} stopClick={true} checked={!!a.isSelected} onChange={e => this.selectSurvey(a, !!e.currentTarget.checked)} /></td>
                                            <td>{formatName(a.name)}</td>
                                            <td>{formatDateLocal(a.birthdate)}</td>
                                            <td>{a.address.city}, {a.address.state}</td>
                                            <td>{a.insurance.provider}</td>
                                            <td className="col-center inline" data-caption="Sex: ">{a.sex}</td>
                                            <td className="col-center inline" data-caption="Has Appointment(s): ">{a.hasAppointment ? <i className="icon-check" /> : null}</td>
                                        </tr>)}
                                    </tbody>
                                </table>
                                &nbsp;
                                <div className="action-panel-dialog">
                                    <button type="button" className="button text-with-icon" onClick={this.loadSurveys}><i className="icon-download" /><span>Fetch</span></button>
                                </div>
                            </div>
                            : this.state.loadProgress < this.state.loadTotal
                                ? <p>Loading surveys, please wait... {this.state.loadProgress} of {this.state.loadTotal} ({Math.round(this.state.loadProgress * 100 / this.state.loadTotal)} %)</p>
                                : <>
                                    <p>{this.state.loadTotal} Survey(s) Loaded</p>
                                    <div className="action-panel-dialog">
                                        <button type="button" className="button text-with-icon" onClick={this.printPage}><i className="icon-print" /><span>Print</span></button>
                                    </div>
                                </>}
                    </div>
                    {this.state.loadError
                        ? <div className="error-text">{this.state.loadError}</div>
                        : null}
                </div>
                <div className="print-only">
                    {this.state.surveys.map(a => {
                        const propsSingle: any = {
                            survey: a
                        };

                        return <form key={a.id} className="viewer-root viewer-print-page" onSubmit={e => e.preventDefault()}>
                            <QuestionnaireViewInner {...propsBase} {...propsSingle} />
                        </form>;
                    })}
                </div>
            </div>
        );
    }

    private setStateAsync = getSetStateAsync<IQuestionnairePrintAllViewState>();

    private selectSurvey(survey: any, selected?: boolean) {
        const { surveySummary } = this.state;

        if (selected !== undefined) {
            survey.isSelected = selected;
        }
        else {
            survey.isSelected = !survey.isSelected;
        }

        this.setState({
            surveySummary
        });
    }

    private selectAllSurveys() {
        const { surveySummary } = this.state;
        const currentlySelected = surveySummary.every(a => a.isSelected);

        for (const item of surveySummary) {
            item.isSelected = !currentlySelected;
        }

        this.setState({
            surveySummary
        });
    }

    private loadSurveys() {
        this.loadSurveysAsync()
            .then(surveys => {
                this.setState(s => ({
                    loadProgress: s.loadTotal,
                    surveys: surveys
                }));
            })
            .catch(e => {
                this.setState({
                    loadError: e.message || "Could not load surveys"
                });
            });
    }

    private async loadSurveysAsync() {
        const ids = this.state.surveySummary
            .filter(a => a.isSelected)
            .map((a: any): string => a.id);
        const surveys: any[] = [];

        await this.setStateAsync({ loadTotal: ids.length });

        let current = 1;
        for (const id of ids) {
            const survey = await V1.get(`survey/${id}?questions=full`);
            surveys.push(survey);

            await this.setStateAsync({ loadProgress: current });
            current++;
        }

        return surveys;
    }

    private printPage() {
        const oldTitle = document.title;
        document.title = `Multiple Questionnaires`;
        window.print();
        document.title = oldTitle;
    }
}

export default withTitle("Print questionnaires", QuestionnairePrintAllView);